import { RadioGroup, SelectInput, TextInput } from '@/components/inputs'
import { booleanOptions } from '@/constants'
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  Stack,
  Typography,
  Alert,
  FormHelperText,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { PartnerFormData } from './partner-form.schema'
import { DateField } from '@/components'
import { useState } from 'react'
import { WarningRounded } from '@mui/icons-material'
import { EDualSocialSecurityIndicator } from '@/types'
import { CNPJ_IMASK_OPTS, getEnumOptions } from '@/helpers'

type PartnerFormProps = {
  isUpdate?: boolean
}
export function PartnerForm({ isUpdate = false }: PartnerFormProps) {
  const { control, watch } = useFormContext<PartnerFormData>()

  const [doubleBoundAlertOpen, setDoubleBoundAlertOpen] = useState(false)

  const [proLaboreType, hasDoubleBond] = watch([
    'pro_labore_type',
    'has_double_bond',
  ])

  const toggleDoubleBoundAlert = () => {
    setDoubleBoundAlertOpen(!doubleBoundAlertOpen)
  }

  return (
    <Stack spacing={2} mt={1}>
      {!isUpdate && <TextInput name="name" control={control} label="Nome" />}

      <TextInput
        name="tax_id"
        control={control}
        label="CPF"
        mask="000.000.000-00"
        disabled={isUpdate}
      />

      <Controller
        name="admission_date"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <DateField
            label="Data de admissão"
            error={!!error}
            helperText={error?.message}
            {...field}
          />
        )}
      />

      <TextInput name="pis" control={control} label="PIS/NIT" />

      <Controller
        name="pro_labore_type"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error}>
            <Typography variant="h6">
              Como deseja realizar a retirada do pró-labore?
            </Typography>
            <Alert severity="warning" icon={false}>
              Esse valor de pró-labore ficará salvo para os próximos meses. Se
              necessário, faça o ajuste do valor até o dia 25 do mês.
            </Alert>
            <MuiRadioGroup {...field}>
              <FormControlLabel
                value="variable"
                control={<Radio />}
                label="Valor variável (Recomendado)"
              />
              {field.value === 'variable' && (
                <Alert severity="info" icon={false}>
                  A Alume calculará mensalmente o valor necessário para reduzir
                  os impostos totais pago pela PJ seguindo o fator R.
                </Alert>
              )}

              <FormControlLabel
                value="fixed"
                control={<Radio />}
                label="Valor fixo mensal"
              />
              {field.value === 'fixed' && (
                <>
                  <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
                    Caso o seu faturamento não seja compatível com o pró-labore
                    fixo solicitado, nossa equipe irá entrar em contato.
                  </Alert>
                  <TextInput
                    name="pro_labore_amount"
                    control={control}
                    label="Pró-labore (R$)"
                    format="currency"
                  />
                </>
              )}

              <FormControlLabel
                value="none"
                control={<Radio />}
                label="Não haverá retirada de pró-labore"
              />
              {field.value === 'none' && (
                <Alert severity="warning" icon={false} sx={{ mb: 2 }}>
                  É necessário cadastrar um pró-labore enquanto sua empresa
                  estiver faturando.
                </Alert>
              )}
            </MuiRadioGroup>
            <FormHelperText>{error?.message}</FormHelperText>
          </FormControl>
        )}
      />

      {proLaboreType !== 'none' && (
        <RadioGroup
          name="has_double_bond"
          isBooleanChoice
          control={control}
          label="Possui duplo vínculo?"
          options={booleanOptions}
          row
          onChange={(event) => {
            if (event.target.value === 'true') {
              setDoubleBoundAlertOpen(true)
            }
          }}
        />
      )}

      {hasDoubleBond && proLaboreType !== 'none' && (
        <>
          <TextInput
            name="inss_base_amount"
            control={control}
            label="Valor da base do INSS (R$)"
            format="currency"
          />

          <TextInput
            name="inss_contribution_amount"
            control={control}
            label="Valor da contribuição do INSS (R$)"
            format="currency"
          />

          <SelectInput
            name="dual_social_security_indicator"
            control={control}
            label="Indicativo de duplo vínculo"
            options={getEnumOptions(EDualSocialSecurityIndicator)}
          />

          <TextInput
            name="second_cnpj_link"
            control={control}
            label="CNPJ do outro vínculo"
            mask={CNPJ_IMASK_OPTS}
          />

          <SelectInput
            name="category_code"
            control={control}
            label="Código da categoria"
            options={categoryCodeOptions}
          />
        </>
      )}

      <Dialog open={doubleBoundAlertOpen} onClose={toggleDoubleBoundAlert}>
        <DialogContent sx={{ textAlign: 'center' }}>
          <WarningRounded color="warning" sx={{ fontSize: 64 }} />
          <Typography variant="h5" mb={1}>
            Atenção
          </Typography>
          Ao selecionar Duplo Vínculo, você deverá nos enviar mensalmente o
          holerite, juntamente com os demais arquivos do movimento mensal.
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button variant="outlined" onClick={toggleDoubleBoundAlert}>
            Estou ciente
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

const categoryCodeOptions = [
  'Empregado - Geral, inclusive o empregado público de administração direta ou indireta contratado pela CLT',
  'Contribuinte individual - Diretor não empregado, sem FGTS',
  'Empregado - Doméstico',
  'Trabalhador temporário - Contrato nos termos da Lei 6.019/1974',
  'Contribuinte individual - Autônomo em geral, exceto os descritos nas demais categorias do contribuinte individual',
  'Contribuinte individual - Transportador autônomo de passageiros',
  'Contribuinte individual - Transportador autônomo de carga',
  'Contribuinte individual - Cooperado de cooperativa de produção',
  'Contribuinte individual - Cooperado de cooperativa de trabalho',
  'Contribuinte individual - Cooperado que presta serviço por intermédio de cooperativa de trabalho',
  'Contribuinte individual - Cooperado de cooperativa de serviço',
  'Contribuinte individual - Contribuinte facultativo',
  'Empregado doméstico - Somente filiados nas regras da Lei 8.619/1994',
  'Empregado - Contrato de trabalho intermitente',
  'Segurado especial',
  'Trabalhador rural',
  'Dirigente Sindical - Informações constante pelo sindicato',
  'Autônomo',
  'Magistrado, membro do Ministério Público ou membro da Justiça do Trabalho ou da Justiça Eleitoral, ou seja, aqueles que atuam em regime previd.',
  'Categoria não identificada ou divergente',
  'Contribuinte individual - Microempreendedor individual - MEI',
  'Religioso, desde que não seja o membro de vida consagrada, de congregação, ou de ordem religiosa',
  'Missionário religioso',
  'Microempreendedor',
]
